/**
 * Login error codes returned by the back in the JSON body of a non-2xx response
 * (`/login/connect`, `/login/mfa-verify`).
 *
 * `LOGIN_TEMPORARILY_UNAVAILABLE` (HTTP 503): the back cannot read its anti-brute-force counters
 * (Redis down) and refuses every new login — fail-closed. It must never be shown as
 * "wrong password" / "invalid code": nothing is wrong with what the user typed.
 */
export const LOGIN_TEMPORARILY_UNAVAILABLE = "LOGIN_TEMPORARILY_UNAVAILABLE"

/**
 * Codes of a refused `/login/connect` whose message is translated under the code itself
 * (`translations/*.json` in grc-back): 503 outage, 403 account lock, 403 IP block.
 */
export const LOGIN_REFUSAL_CODES: readonly string[] = [
  LOGIN_TEMPORARILY_UNAVAILABLE,
  "ACCOUNT_LOCKED",
  "IP_BLOCKED",
]

export const LOGIN_TEMPORARILY_UNAVAILABLE_FALLBACK =
  "Login is temporarily unavailable. Please try again in a few minutes."

/** Translation key to display for a failed `/login/mfa-verify` call, from the back's `code`. */
export const mfaVerifyErrorKey = (code?: string | null): string => {
  if (code === LOGIN_TEMPORARILY_UNAVAILABLE) return LOGIN_TEMPORARILY_UNAVAILABLE
  if (code === "ACCOUNT_LOCKED") return "MFA_ACCOUNT_LOCKED"
  return "MFA_INVALID_CODE"
}

/** Text of an MFA error key: translation first, then an English fallback that matches the key. */
export const mfaErrorText = (
  trad: Record<string, string> | undefined | null,
  error: string
): string =>
  trad?.[error] ||
  (error === LOGIN_TEMPORARILY_UNAVAILABLE
    ? LOGIN_TEMPORARILY_UNAVAILABLE_FALLBACK
    : trad?.MFA_INVALID_CODE || "Invalid code")

/**
 * `code` carried by the JSON body of a failed `request()` call. `Utils/request` throws an `Error`
 * whose message is only the HTTP status text ("Service Unavailable") and keeps the unread
 * `Response` on `error.response`. Returns `undefined` when there is no readable JSON body.
 */
export const readErrorCode = async (err: unknown): Promise<string | undefined> => {
  const response = (err as { response?: { json?: () => Promise<unknown> } } | null)?.response
  if (!response || typeof response.json !== "function") return undefined
  try {
    const body = (await response.json()) as { code?: unknown } | null
    return typeof body?.code === "string" ? body.code : undefined
  } catch {
    return undefined
  }
}
